import type { InboxForemanFacet } from '@coffman-tm/shared';
import { api } from './api';
import { useApiQuery } from './useApiQuery';

export type { InboxForemanFacet };

export interface InboxFilters {
  /** A pco_id, the `'unassigned'` sentinel, or null/omitted for no filter. */
  pcoId?: string | null;
  /** Exact `daily_pdfs.foreman_name`, or null/omitted for no filter. */
  foreman?: string | null;
  /**
   * Also fetch the decided PDFs the work pile hides that still carry a
   * skipped block. Off by default: the inbox is a work queue, and these are
   * finished reports the PM asks for by name.
   */
  includeSkipped?: boolean;
}

/**
 * PDF-Import Inbox — Joey's primary surface. Returns the list of PDFs with
 * at least one block_status='pending' block, plus per-PDF status counts.
 * `filters` narrows the list — pco_id and foreman COMPOSE (AND) into the
 * same request. Facets are cross-filtered server-side (each dimension's
 * counts reflect the OTHER dimension's current filter), so they come back
 * fresh on every filter change rather than being cached client-side.
 */
export function useInbox(filters: InboxFilters = {}) {
  const pcoId = filters.pcoId ?? null;
  const foreman = filters.foreman ?? null;
  const includeSkipped = filters.includeSkipped ?? false;
  // Poll every 30s (visible tab only) + refetch on tab-focus so a foreman
  // forward that lands while Joey's on the page shows up without a manual
  // reload. Data stays visible during refetch — no "Loading…" flash.
  const query = useApiQuery(
    (t, signal) => api.inbox.list(t, { pcoId, foreman, includeSkipped }, { signal }),
    [pcoId, foreman, includeSkipped],
    { pollMs: 30_000 },
  );
  const pcoFacets = query.data?.pco_facets ?? [];
  const foremanFacets = query.data?.foreman_facets ?? [];
  const unassignedCount = query.data?.unassigned_count ?? 0;
  // Absent on an API that predates the reveal (Pages ships on push, the Worker
  // is its own deploy): 0 renders no affordance at all, which is the honest
  // degradation — the old behavior, not a "0 skipped reports" line.
  const skippedCount = query.data?.skipped_count ?? 0;
  const skippedPdfs = query.data?.skipped_pdfs ?? [];
  return { ...query, pcoFacets, foremanFacets, unassignedCount, skippedCount, skippedPdfs };
}

/**
 * Full per-block detail for one PDF. Pass `null` to skip the fetch (used
 * when no card is currently expanded).
 */
export function useInboxPdfDetail(dailyPdfId: string | null) {
  return useApiQuery(
    async (t, signal) => {
      if (!dailyPdfId) return null;
      return api.inbox.pdfDetail(t, dailyPdfId, { signal });
    },
    [dailyPdfId],
  );
}
